import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/constants/query-keys';
import { useInfiniteList } from '@/hooks/useInfiniteList';
import type { PageRequest } from '@/types/api.types';
import { saveBlob } from '@/utils/file';

import {
  type CollectedRange,
  type CollectionsRange,
  createInvoiceShareLink,
  getInvoice,
  getInvoiceCollections,
  getInvoicePdf,
  getInvoiceSummary,
  type InvoiceListFilter,
  listInvoices,
  listMemberInvoices,
  publicBillUrl,
  recordPayment,
} from '../api/billing.api';
import type { InvoiceDetail, RecordPaymentFormInput } from '../schemas/invoice.schema';
import { invoiceWhatsappMessage, whatsappUrl } from '../utils/invoice-whatsapp';

export function useInvoices(filter: InvoiceListFilter, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.invoices.list({ ...filter }),
    queryFn: ({ signal }) => listInvoices(filter, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useMemberInvoices(memberId: number, page: PageRequest, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.invoices.byMember(memberId, { ...page }),
    queryFn: ({ signal }) => listMemberInvoices(memberId, page, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useInvoice(id: number | null) {
  return useQuery({
    queryKey: QUERY_KEYS.invoices.detail(id ?? 0),
    queryFn: ({ signal }) => getInvoice(id ?? 0, signal),
    enabled: id !== null,
  });
}

/**
 * The dashboard's money figures: what is still due, and what came in over `range` (this calendar
 * month when it is left out). Zeros, never blanks, when there are no bills.
 */
export function useInvoiceSummary(enabled = true, range?: CollectedRange) {
  return useQuery({
    queryKey: QUERY_KEYS.invoices.summary(range ? { ...range } : undefined),
    queryFn: ({ signal }) => getInvoiceSummary(range, signal),
    enabled,
    // The period picker swaps ranges often; keeping the last figure on screen while the next
    // one loads stops the card flashing empty on every tap.
    placeholderData: (previous) => previous,
  });
}

/** The Bills screen's totals for `range` (all time when both ends are left out). */
export function useInvoiceCollections(range: CollectionsRange) {
  return useQuery({
    queryKey: QUERY_KEYS.invoices.collections({ from: range.from, to: range.to }),
    queryFn: ({ signal }) => getInvoiceCollections(range, signal),
    // Keep the last figures on screen while the next period loads, so the cards don't flash empty.
    placeholderData: (previous) => previous,
  });
}

/**
 * Gets the server-rendered PDF and saves it under the name the server chose (the member, their
 * Club29 code and the bill number) — read from the response, so it is decided in one place.
 */
export function useDownloadInvoicePdf() {
  return useMutation({
    mutationFn: async ({ id, fallbackName }: { id: number; fallbackName: string }) => {
      const { blob, fileName } = await getInvoicePdf(id);
      saveBlob(blob, fileName ?? fallbackName);
    },
  });
}

/**
 * Makes the bill's public link and returns the WhatsApp address that opens the member's chat with
 * the message — and the link in it — already typed. The staff member only has to press send.
 */
export function useWhatsappInvoiceLink() {
  return useMutation({
    mutationFn: async (invoice: InvoiceDetail) => {
      const { token } = await createInvoiceShareLink(invoice.id);
      return whatsappUrl(
        invoice.member.mobile,
        invoiceWhatsappMessage(invoice, publicBillUrl(token)),
      );
    },
  });
}

export function useRecordPayment(invoiceId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordPaymentFormInput) => recordPayment(invoiceId, input),
    // Every bill list, the bill itself and the dashboard figure all move with one payment.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all }),
  });
}

/** Rows fetched per page by the lists that load as the user scrolls. */
const FEED_PAGE_SIZE = 10;

/** Bills loaded as the user scrolls a list inside a card (the dashboard's "Money due"). */
export function useInvoiceFeed(
  filter: Omit<InvoiceListFilter, 'page' | 'pageSize'>,
  enabled = true,
) {
  return useInfiniteList({
    queryKey: QUERY_KEYS.invoices.feed({ ...filter }),
    fetchPage: (page, signal) =>
      listInvoices({ ...filter, page, pageSize: FEED_PAGE_SIZE }, signal),
    enabled,
  });
}

/** A member's bills, newest first, loaded as the user scrolls the profile's Bills card. */
export function useMemberInvoiceFeed(memberId: number) {
  return useInfiniteList({
    queryKey: QUERY_KEYS.invoices.feed({ memberId, scope: 'member' }),
    fetchPage: (page, signal) =>
      listMemberInvoices(memberId, { page, pageSize: FEED_PAGE_SIZE }, signal),
  });
}
